import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../../services/api';

export default function Recipes() {
  const [recipes, setRecipes] = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch]   = useState('');
  const [category, setCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [usersMap, setUsersMap]     = useState({});
  const [offset, setOffset]   = useState(0);
  const [error, setError]     = useState('');
  const [confirmDeleteRecipe, setConfirmDeleteRecipe] = useState(null);
  const [searchParams] = useSearchParams();
  const userIdFilter = searchParams.get('user_id');
  const LIMIT = 25;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      let data;
      if (search.trim()) {
        data = await api.searchRecipes(search.trim());
        setRecipes(data.recipes || []);
        setTotal(data.total ?? (data.recipes || []).length);
      } else {
        const params = { limit: LIMIT, offset };
        if (category) params.category = category;
        if (userIdFilter) params.user_id = userIdFilter;
        data = await api.getRecipes(params);
        setRecipes(data.recipes || []);
        setTotal(data.total || 0);
      }
    } catch(e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [search, category, offset, userIdFilter]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.getCategories().then(d => setCategories(d.categories || [])).catch(() => {});
    api.getUsers().then(d => {
      const map = {};
      (d.users || []).forEach(u => { map[u.id] = u; });
      setUsersMap(map);
    }).catch(() => {});
  }, []);

  async function togglePublic(recipe) {
    setError('');
    try {
      await api.updateRecipe(recipe.id, { is_public: !recipe.is_public });
      setRecipes(prev => prev.map(r => r.id === recipe.id ? { ...r, is_public: !r.is_public } : r));
    } catch(e) { setError(e.message); }
  }

  async function handleDelete(recipe) {
    setConfirmDeleteRecipe(null);
    setError('');
    try {
      await api.deleteRecipe(recipe.id);
      await load();
    } catch(e) { setError(e.message); }
  }

  const s = {
    wrap:    { padding: '28px 32px', maxWidth: 1200 },
    h1:      { fontSize: '1.4rem', color: 'var(--admin-text)', fontFamily: 'Inter,sans-serif', fontWeight: 600, marginBottom: 6 },
    sub:     { fontSize: '.78rem', color: 'var(--admin-text-dim)', marginBottom: 24 },
    toolbar: { display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' },
    inp:     { background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '7px 12px', fontSize: '.78rem', flex: 1, minWidth: 200 },
    sel:     { background: 'var(--admin-bg-input)', border: '1px solid var(--admin-border)', borderRadius: 5, color: 'var(--admin-text)', padding: '7px 10px', fontSize: '.78rem' },
    secLbl:  { fontSize: '.7rem', fontWeight: 600, color: 'var(--admin-text-dim)', textTransform: 'uppercase', letterSpacing: '.08em' },
    table:   { width: '100%', borderCollapse: 'collapse', fontSize: '.78rem', background: 'var(--admin-bg)', border: '1px solid var(--admin-border)', borderRadius: 8, overflow: 'hidden' },
    th:      { padding: '10px 14px', textAlign: 'left', color: 'var(--admin-text-dim)', fontWeight: 600, background: 'var(--admin-bg-dark)', borderBottom: '1px solid var(--admin-border)', whiteSpace: 'nowrap' },
    tdFn:    (i) => ({ padding: '9px 14px', borderBottom: '1px solid var(--admin-border)', background: i % 2 === 0 ? 'var(--admin-bg)' : 'var(--admin-bg-alt)', verticalAlign: 'middle' }),
    btnEdt:  { background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 4, padding: '3px 9px', fontSize: '.68rem', cursor: 'pointer', textDecoration: 'none', display: 'inline-block' },
    btnDel:  { background: 'none', border: '1px solid var(--admin-danger)', color: 'var(--admin-danger)', borderRadius: 4, padding: '3px 9px', fontSize: '.68rem', cursor: 'pointer', marginLeft: 5 },
    badge:   (pub) => ({ display: 'inline-block', padding: '2px 8px', borderRadius: 20, fontSize: '.65rem', fontWeight: 600, cursor: 'pointer', background: pub ? 'color-mix(in srgb, var(--admin-success) 15%, transparent)' : 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', color: pub ? 'var(--admin-success)' : 'var(--admin-danger)', border: `1px solid ${pub ? 'var(--admin-success)' : 'var(--admin-danger)'}` }),
    pager:   { display: 'flex', gap: 8, marginTop: 14, alignItems: 'center', fontSize: '.75rem', color: 'var(--admin-text-dim)' },
    pbtn:    (dis) => ({ background: 'none', border: '1px solid var(--admin-border)', color: dis ? 'var(--admin-border)' : 'var(--admin-text-muted)', borderRadius: 4, padding: '4px 12px', fontSize: '.72rem', cursor: dis ? 'default' : 'pointer' }),
  };

  return (
    <div style={s.wrap}>
      <h1 style={s.h1}>Rezepte-Verwaltung</h1>
      {userIdFilter ? (
        <p style={{...s.sub, color:'var(--admin-link)'}}>
          Gefiltert nach {usersMap[userIdFilter] ? <strong>{usersMap[userIdFilter].username}</strong> : `Benutzer #${userIdFilter}`} &nbsp;·&nbsp;
          <Link to="/admin/recipes" style={{color:'var(--admin-link)'}}>Filter entfernen →</Link>
        </p>
      ) : (
        <p style={s.sub}>Alle Rezepte einsehen, bearbeiten, sichtbar/unsichtbar schalten und löschen.</p>
      )}

      {error && (
        <div style={{ background: 'color-mix(in srgb, var(--admin-danger) 10%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 14px', color: 'var(--admin-danger)', fontSize: '.78rem', marginBottom: 16 }}>
          {error}
        </div>
      )}

      {confirmDeleteRecipe && (
        <div style={{ background: 'color-mix(in srgb, var(--admin-danger) 8%, transparent)', border: '1px solid var(--admin-danger)', borderRadius: 6, padding: '10px 16px', marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'space-between', fontSize: '.78rem', color: 'var(--admin-text)' }}>
          <span>Rezept <strong>„{confirmDeleteRecipe.title}"</strong> wirklich löschen?</span>
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ background: 'var(--admin-danger)', border: 'none', color: '#fff', borderRadius: 4, padding: '4px 12px', fontSize: '.72rem', cursor: 'pointer' }}
              onClick={() => handleDelete(confirmDeleteRecipe)}>Ja, löschen</button>
            <button style={{ background: 'none', border: '1px solid var(--admin-border)', color: 'var(--admin-text-muted)', borderRadius: 4, padding: '4px 12px', fontSize: '.72rem', cursor: 'pointer' }}
              onClick={() => setConfirmDeleteRecipe(null)}>Abbrechen</button>
          </div>
        </div>
      )}

      <div style={s.toolbar}>
        <input
          style={s.inp}
          placeholder="🔍 Rezept suchen…"
          value={search}
          onChange={e => { setSearch(e.target.value); setOffset(0); }}
        />
        <select style={s.sel} value={category} onChange={e => { setCategory(e.target.value); setOffset(0); }}>
          <option value="">Alle Kategorien</option>
          {categories.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        <span style={s.secLbl}>{total} Rezepte</span>
      </div>

      {loading ? (
        <p style={{color:'var(--admin-text-dim)',fontSize:'.78rem'}}>Lade...</p>
      ) : recipes.length === 0 ? (
        <p style={{color:'var(--admin-text-dim)',fontSize:'.78rem'}}>Keine Rezepte gefunden.</p>
      ) : (
        <>
          <table style={s.table}>
            <thead>
              <tr>
                {['Titel','Kategorie','Ersteller','Portionen','Sichtbar','Aktionen'].map(h => (
                  <th key={h} style={s.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recipes.map((r, i) => (
                <tr key={r.id}>
                  <td style={s.tdFn(i)}>
                    <span style={{color:'var(--admin-text)',fontWeight:600}}>{r.title}</span>
                    {(() => { const tags = Array.isArray(r.tags) ? r.tags : (typeof r.tags === 'string' ? JSON.parse(r.tags || '[]') : []); return tags.length > 0 && (
                      <div style={{marginTop:3}}>
                        {tags.slice(0,3).map(t => (
                          <span key={t} style={{fontSize:'.6rem',color:'var(--admin-text-dim)',background:'var(--admin-tag-bg)',borderRadius:3,padding:'1px 5px',marginRight:3}}>{t}</span>
                        ))}
                      </div>
                    ); })()}
                  </td>
                  <td style={{...s.tdFn(i),color:'var(--admin-text-muted)'}}>{r.category || '—'}</td>
                  <td style={s.tdFn(i)}>
                    {usersMap[r.user_id] ? (
                      <Link to={`/admin/users/edit/${r.user_id}`} style={{color:'var(--admin-link)',textDecoration:'none',fontSize:'.75rem'}}>
                        {usersMap[r.user_id].username}
                        <span style={{color:'var(--admin-text-dim)',marginLeft:5,fontSize:'.65rem'}}>#{r.user_id}</span>
                      </Link>
                    ) : (
                      <span style={{color:'var(--admin-text-dim)',fontSize:'.75rem'}}>#{r.user_id}</span>
                    )}
                  </td>
                  <td style={{...s.tdFn(i),color:'var(--admin-text-muted)',textAlign:'center'}}>{r.servings || '—'}</td>
                  <td style={s.tdFn(i)}>
                    <span style={s.badge(r.is_public)} onClick={() => togglePublic(r)} title="Klicken zum Umschalten">
                      {r.is_public ? '✓ Öffentlich' : '✕ Privat'}
                    </span>
                  </td>
                  <td style={s.tdFn(i)}>
                    <Link to={`/admin/recipes/edit/${r.id}`} style={s.btnEdt}>✏ Bearbeiten</Link>
                    <button style={s.btnDel} onClick={() => { setError(''); setConfirmDeleteRecipe(r); }}>✕ Löschen</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {!search && (
            <div style={s.pager}>
              <button style={s.pbtn(offset === 0)} disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))}>← Zurück</button>
              <span>{offset + 1}–{Math.min(offset + LIMIT, total)} von {total}</span>
              <button style={s.pbtn(offset + LIMIT >= total)} disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)}>Weiter →</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
